import React, { useState, useEffect, useRef } from 'react';
import { formatDate, formatDateLong } from '../../utils/dateUtils';
import {
  X,
  Save,
  AlertCircle,
  Calendar,
  DollarSign,
  ShoppingBag,
  ArrowRight,
  Edit2,
  Trash2,
  Plus,
  Receipt,
  CheckCircle,
  Wand2,
  Eye,
  Edit,
  Link2Off,
  Loader,
  Clock,
  ArrowRightLeft,
  GripVertical,
  ChevronUp,
  ChevronDown,
  ArrowLeft,
  Star,
  AlertTriangle,
  Tag
} from 'lucide-react';
import apiService from '../../services/api';
import { useConfirmation } from '../../hooks/useConfirmation';
import { useToast } from '../../hooks/useToast';
import BaseModal from '../common/BaseModal';
import ReceiptVersionHistoryModal from './ReceiptVersionHistoryModal';
import AuthImage from '../common/AuthImage';
import LinkModal from './LinkModal';

const ReceiptModal = ({ isOpen, onClose, receipt, accounts, currentAccount, onReceiptUpdated, onViewTransaction, initialEditMode = false }) => {
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [merchant, setMerchant] = useState('');
  const [receiptDate, setReceiptDate] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [error, setError] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // Importance tracking fields
  const [importanceLevel, setImportanceLevel] = useState('normal');
  const [expirationDate, setExpirationDate] = useState('');
  const [importanceNotes, setImportanceNotes] = useState('');
  const [tags, setTags] = useState([]);
  const [isImportanceSectionExpanded, setIsImportanceSectionExpanded] = useState(false);

  const { confirm, ConfirmationModal } = useConfirmation();
  const { showToast, ToastContainer } = useToast();
  const newItemInputRef = useRef(null);
  const itemsContainerRef = useRef(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (receipt) {
      setMerchant(receipt.merchant || '');
      setReceiptDate(receipt.receipt_date?.split('T')[0] || '');
      setSelectedAccountId(currentAccount?.id);
      setItems(receipt.items || []);
      setIsEditMode(initialEditMode); // Use initial edit mode prop
      setShowVersionHistory(false); // Reset version history modal state

      // Initialize importance fields
      setImportanceLevel(receipt.importance_level || 'normal');
      setExpirationDate(receipt.expiration_date?.split('T')[0] || '');
      setImportanceNotes(receipt.importance_notes || '');
      setTags(receipt.tags || []);
    }
  }, [receipt, currentAccount, initialEditMode]);

  // Reset modal states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowVersionHistory(false);
      setIsLinkModalOpen(false);
      setIsLightboxOpen(false);
    }
  }, [isOpen]);

  // Handle ESC key for lightbox
  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        setIsLightboxOpen(false);
      }
    };

    // Add listener with capture to intercept before BaseModal
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isLightboxOpen]);

  const loadCategories = async () => {
    try {
      const categories = await apiService.getCategories();
      const options = categories.map(cat => ({
        value: cat.name,
        label: cat.label,
        icon: cat.icon
      }));
      setCategoryOptions(options);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const totalAmount = items.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);

  const handleUpdateItem = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    // Recalculate total_price if quantity or unit_price changes
    if (field === 'quantity' || field === 'unit_price') {
      const quantity = parseFloat(updatedItems[index].quantity) || 0;
      const unitPrice = parseFloat(updatedItems[index].unit_price) || 0;
      updatedItems[index].total_price = (quantity * unitPrice).toFixed(2);
    }

    // Update category icon when category changes
    if (field === 'category') {
      const selectedCategory = categoryOptions.find(cat => cat.value === value);
      if (selectedCategory) {
        updatedItems[index].category_icon = selectedCategory.icon;
      }
    }

    setItems(updatedItems);
  };

  const handleDeleteItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        description: '',
        quantity: 1,
        unit_price: 0,
        total_price: 0,
        category: '',
        category_icon: ''
      }
    ]);

    // Focus the new item's description field after the state updates
    setTimeout(() => {
      if (newItemInputRef.current) {
        newItemInputRef.current.focus();
        newItemInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleMoveItem = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    const updatedItems = [...items];
    const [movedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, movedItem);
    setItems(updatedItems);
  };

  const handleMoveUp = (index, e) => {
    e?.stopPropagation();
    if (index > 0) {
      const button = e.currentTarget;
      const buttonRect = button.getBoundingClientRect();
      const mouseY = buttonRect.top + buttonRect.height / 2;

      handleMoveItem(index, index - 1);

      // After React re-renders, find the button at the new position and simulate hover
      setTimeout(() => {
        const container = itemsContainerRef.current;
        if (container) {
          const allButtons = container.querySelectorAll('button[title="Move up"]');
          const newButton = allButtons[index - 1];
          if (newButton) {
            const newRect = newButton.getBoundingClientRect();
            // Scroll so the new button is at the same Y position as the old one
            const scrollDiff = newRect.top - mouseY;
            container.scrollTop += scrollDiff;
          }
        }
      }, 10);
    }
  };

  const handleMoveDown = (index, e) => {
    e?.stopPropagation();
    if (index < items.length - 1) {
      const button = e.currentTarget;
      const buttonRect = button.getBoundingClientRect();
      const mouseY = buttonRect.top + buttonRect.height / 2;

      handleMoveItem(index, index + 1);

      // After React re-renders, find the button at the new position and simulate hover
      setTimeout(() => {
        const container = itemsContainerRef.current;
        if (container) {
          const allButtons = container.querySelectorAll('button[title="Move down"]');
          const newButton = allButtons[index + 1];
          if (newButton) {
            const newRect = newButton.getBoundingClientRect();
            // Scroll so the new button is at the same Y position as the old one
            const scrollDiff = newRect.top - mouseY;
            container.scrollTop += scrollDiff;
          }
        }
      }, 10);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedIndex === null || draggedIndex === index) return;

    setDragOverIndex(index);

    // Simple auto-scroll
    if (itemsContainerRef.current) {
      const container = itemsContainerRef.current;
      const rect = container.getBoundingClientRect();
      const threshold = 100;

      if (e.clientY > rect.bottom - threshold) {
        container.scrollTop += 15;
      } else if (e.clientY < rect.top + threshold) {
        container.scrollTop -= 15;
      }
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      handleMoveItem(draggedIndex, index);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleAiCategorize = async () => {
    try {
      setCategorizing(true);
      setError(null);

      // Queue AI categorization task
      const response = await apiService.request(`/receipts/${receipt.id}/ai-categorize`, {
        method: 'POST'
      });

      const jobId = response.job_id;

      // Poll for job completion
      const pollStatus = async () => {
        const statusResponse = await apiService.request(
          `/receipts/${receipt.id}/ai-categorize-status/${jobId}`
        );

        if (statusResponse.status === 'completed') {
          // Update items with new categories
          setItems(statusResponse.items || []);
          setCategorizing(false);
        } else if (statusResponse.status === 'failed') {
          setError(statusResponse.message || 'AI categorization failed');
          setCategorizing(false);
        } else {
          // Still processing, poll again in 1 second
          setTimeout(pollStatus, 1000);
        }
      };

      // Start polling
      setTimeout(pollStatus, 1000);

    } catch (err) {
      console.error('Error categorizing items:', err);
      setError(err.response?.data?.detail || 'Failed to categorize items with AI');
      setCategorizing(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!merchant.trim()) {
        setError('Merchant name is required');
        return;
      }

      if (!receiptDate) {
        setError('Receipt date is required');
        return;
      }

      if (items.length === 0) {
        setError('At least one item is required');
        return;
      }

      // Validate all items have descriptions
      for (const item of items) {
        if (!item.description?.trim()) {
          setError('All items must have a description');
          return;
        }
      }

      const updateData = {
        receipt_id: receipt.id,
        merchant,
        receipt_date: receiptDate,
        total_amount: totalAmount,
        items: items.map((item, idx) => ({
          id: item.id,
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          total_price: parseFloat(item.total_price) || 0,
          category: item.category,
          category_icon: item.category_icon,
          line_number: idx + 1
        })),
        new_account_id: selectedAccountId !== currentAccount?.id ? selectedAccountId : null,
        // Importance tracking fields
        importance_level: importanceLevel,
        expiration_date: expirationDate || null,
        importance_notes: importanceNotes || null,
        tags: tags
      };

      await apiService.updateReceipt(updateData);
      setIsEditMode(false); // Exit edit mode after saving
      onReceiptUpdated();
    } catch (err) {
      console.error('Error updating receipt:', err);
      setError(err.response?.data?.detail || 'Failed to update receipt');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    const confirmed = await confirm(
      'Unlink Receipt',
      'Are you sure you want to unlink this receipt from its transaction?',
      'warning'
    );
    if (!confirmed) return;

    try {
      setUnlinking(true);
      await apiService.unlinkReceipt(receipt.id);
      showToast('Receipt unlinked successfully!', 'success');
      onReceiptUpdated?.();
      onClose();
    } catch (error) {
      console.error('Error unlinking receipt:', error);
      showToast('Failed to unlink receipt. Please try again.', 'error');
    } finally {
      setUnlinking(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currentAccount?.currency || 'USD'
    }).format(amount || 0);
  };

  // formatDate/formatDateLong imported from utils/dateUtils
  const formatDateDisplay = (dateString) => {
    if (!dateString) return 'Unknown date';
    return formatDateLong(dateString);
  };

  const selectedAccount = accounts?.find(a => a.id === selectedAccountId);
  const isMovingAccount = selectedAccountId !== currentAccount?.id;

  if (!receipt) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={saving || categorizing || unlinking ? () => {} : onClose}
      size="xl"
      showCloseButton={false}
      closeOnBackdrop={false}
      resizable={true}
    >
      {/* Header — view-mode action buttons live here alongside Edit/X so
          they're reachable without scrolling to the bottom of a long receipt. */}
      <div className="px-4 md:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <Receipt className="h-5 w-5 md:h-6 md:w-6 text-green-600 dark:text-green-500 flex-shrink-0" />
          <h3 className="text-base md:text-lg font-medium text-gray-900 dark:text-white truncate">
            {isEditMode ? 'Edit Receipt' : 'Receipt Details'}
          </h3>
        </div>
        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
          {!isEditMode && (
            <>
              <button
                type="button"
                onClick={() => setShowVersionHistory(true)}
                title="Version History"
                className="inline-flex items-center px-2 md:px-3 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/30 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 rounded-md transition-colors"
              >
                <Clock className="h-4 w-4" />
                <span className="hidden md:inline ml-1.5">History</span>
              </button>
              {!receipt.transaction_id ? (
                <button
                  type="button"
                  onClick={() => setIsLinkModalOpen(true)}
                  title="Match to Transaction"
                  className="inline-flex items-center px-2 md:px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-md transition-colors"
                >
                  <ArrowRightLeft className="h-4 w-4" />
                  <span className="hidden md:inline ml-1.5">Match</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleUnlink}
                  disabled={unlinking}
                  title="Unlink from Transaction"
                  className="inline-flex items-center px-2 md:px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
                >
                  {unlinking ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Link2Off className="h-4 w-4" />
                  )}
                  <span className="hidden md:inline ml-1.5">{unlinking ? 'Unlinking…' : 'Unlink'}</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsEditMode(true)}
                title="Edit Receipt"
                className="inline-flex items-center px-2 md:px-3 py-1.5 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-md transition-colors"
              >
                <Edit2 className="h-4 w-4" />
                <span className="hidden md:inline ml-1.5">Edit</span>
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 ml-1"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>

        {/* Body */}
        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-400 mr-2" />
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              </div>
            </div>
          )}

          {/* Receipt Photo */}
          {receipt?.receipt_image_url && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                <Eye className="h-4 w-4 mr-2" />
                Receipt Photo
              </h3>
              <div className="relative" onClick={() => setIsLightboxOpen(true)}>
                <AuthImage
                  src={receipt.receipt_image_url}
                  alt="Receipt"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 max-h-96 object-contain bg-white cursor-pointer hover:opacity-90 transition-opacity"
                  fallback={
                    <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
                      Unable to load receipt image
                    </div>
                  }
                />
              </div>
              <button
                onClick={() => setIsLightboxOpen(true)}
                className="mt-2 inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                <Eye className="h-3 w-3 mr-1" />
                View full size
              </button>
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <ShoppingBag className="h-4 w-4 inline mr-1" />
                Merchant
              </label>
              {isEditMode ? (
                <input
                  type="text"
                  value={merchant}
                  onChange={(e) => setMerchant(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Store name"
                />
              ) : (
                <p className="text-base text-gray-900 dark:text-white font-medium px-3 py-2">
                  {merchant || 'Unknown Merchant'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Date
              </label>
              {isEditMode ? (
                <input
                  type="date"
                  value={receiptDate}
                  onChange={(e) => setReceiptDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              ) : (
                <p className="text-base text-gray-900 dark:text-white px-3 py-2">
                  {formatDate(receiptDate)}
                </p>
              )}
            </div>
          </div>

          {/* Account Selection (Edit Mode Only) */}
          {isEditMode && accounts && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Account
              </label>
              <select
                value={selectedAccountId || ''}
                onChange={(e) => setSelectedAccountId(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.institution})
                  </option>
                ))}
              </select>
              {isMovingAccount && (
                <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                  <div className="flex items-center text-sm text-blue-700 dark:text-blue-300">
                    <ArrowRight className="h-4 w-4 mr-2" />
                    <span>
                      Moving from <strong>{currentAccount?.name}</strong> to <strong>{selectedAccount?.name}</strong>
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Linked Transaction Info (View Mode Only) */}
          {!isEditMode && receipt.transaction_id && receipt.transaction_description && (
            <div className="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-md">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-500" />
                <span className="font-medium text-green-900 dark:text-green-200">Linked to transaction:</span>
                {onViewTransaction ? (
                  <button
                    type="button"
                    onClick={() => onViewTransaction(receipt.transaction_id)}
                    className="text-green-700 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:underline font-medium"
                  >
                    {receipt.transaction_description}
                  </button>
                ) : (
                  <span className="text-green-700 dark:text-green-400 font-medium">
                    {receipt.transaction_description}
                  </span>
                )}
              </div>
            </div>
          )}
          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Items ({items.length})
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleAiCategorize}
                  disabled={categorizing}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 disabled:opacity-50"
                  title="Use AI to categorize all items"
                >
                  {categorizing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-700 dark:border-purple-300 mr-1"></div>
                      Categorizing...
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-4 w-4 mr-1" />
                      AI Categorize
                    </>
                  )}
                </button>
                {isEditMode && (
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Item
                  </button>
                )}
              </div>
            </div>

            {isEditMode ? (
              /* Edit Mode - Editable Items */
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div ref={itemsContainerRef} className="max-h-96 overflow-y-auto">
                  {items.map((item, index) => (
                    <div
                      key={index}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      className={`border-b border-gray-200 dark:border-gray-700 last:border-b-0 p-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-all ${
                        draggedIndex === index ? 'opacity-50' : ''
                      } ${
                        dragOverIndex === index ? 'border-t-2 border-t-blue-500' : ''
                      }`}
                    >
                      <div className="grid grid-cols-12 gap-2 items-center">
                        {/* Drag Handle */}
                        <div
                          className="col-span-1 flex items-center justify-center"
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragEnd={handleDragEnd}
                        >
                          <div className="cursor-move text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" title="Drag to reorder">
                            <GripVertical className="h-4 w-4" />
                          </div>
                        </div>
                        {/* Description */}
                        <div className="col-span-11 sm:col-span-3">
                          <input
                            ref={index === items.length - 1 ? newItemInputRef : null}
                            type="text"
                            value={item.description}
                            onChange={(e) => handleUpdateItem(index, 'description', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                            placeholder="Item name"
                          />
                        </div>

                        {/* Category */}
                        <div className="col-span-5 sm:col-span-2">
                          <select
                            value={item.category || ''}
                            onChange={(e) => handleUpdateItem(index, 'category', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                          >
                            <option value="">Category</option>
                            {categoryOptions.map(cat => (
                              <option key={cat.value} value={cat.value}>
                                {cat.icon} {cat.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Quantity */}
                        <div className="col-span-2 sm:col-span-1">
                          <input
                            type="number"
                            step="0.001"
                            value={item.quantity}
                            onChange={(e) => handleUpdateItem(index, 'quantity', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                            placeholder="Qty"
                          />
                        </div>

                        {/* Unit Price */}
                        <div className="col-span-2 sm:col-span-2">
                          <input
                            type="number"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => handleUpdateItem(index, 'unit_price', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                            placeholder="Price"
                          />
                        </div>

                        {/* Total Price */}
                        <div className="col-span-2 sm:col-span-1">
                          <div className="px-2 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded font-medium text-right">
                            ${parseFloat(item.total_price || 0).toFixed(2)}
                          </div>
                        </div>

                        {/* Action Buttons: Move Up/Down & Delete */}
                        <div className="col-span-12 sm:col-span-2 flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={(e) => handleMoveUp(index, e)}
                            disabled={index === 0}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            title="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => handleMoveDown(index, e)}
                            disabled={index === items.length - 1}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            title="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteItem(index)}
                            className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded ml-1"
                            title="Delete item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* View Mode - Read-only Items */
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  {items && items.length > 0 ? (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            {item.category_icon && (
                              <span className="text-xl flex-shrink-0">{item.category_icon}</span>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {item.description}
                                </span>
                                {item.quantity > 1 && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    ×{item.quantity}
                                  </span>
                                )}
                              </div>
                              {item.category && (
                                <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
                                  {item.category}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="font-semibold text-sm text-gray-900 dark:text-white ml-4 flex-shrink-0">
                            {formatCurrency(item.total_price)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      No items found
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Importance Details - Collapsible (Edit Mode) */}
            {isEditMode && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setIsImportanceSectionExpanded(!isImportanceSectionExpanded)}
                  className="w-full flex items-center justify-between px-4 py-2 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-md hover:bg-yellow-100 dark:hover:bg-yellow-900/20 transition-colors"
                >
                  <div className="flex items-center">
                    <Star className="h-4 w-4 mr-2 text-yellow-600" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Importance Details</span>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-gray-600 dark:text-gray-400 transition-transform ${isImportanceSectionExpanded ? 'transform rotate-180' : ''}`} />
                </button>

                {isImportanceSectionExpanded && (
                  <div className="mt-2 p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Importance Level */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <AlertTriangle className="h-4 w-4 inline mr-1" />
                          Importance Level
                        </label>
                        <select
                          value={importanceLevel}
                          onChange={(e) => setImportanceLevel(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        >
                          <option value="normal">Normal</option>
                          <option value="important">Important</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>

                      {/* Expiration Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <Calendar className="h-4 w-4 inline mr-1" />
                          Expiration Date (Optional)
                        </label>
                        <input
                          type="date"
                          value={expirationDate}
                          onChange={(e) => setExpirationDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          placeholder="Warranty expiration date"
                        />
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Tag className="h-4 w-4 inline mr-1" />
                        Tags
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {['Warranty', 'Tax', 'Legal', 'Return Policy', 'Insurance', 'Important'].map(tag => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => {
                              if (tags.includes(tag)) {
                                setTags(tags.filter(t => t !== tag));
                              } else {
                                setTags([...tags, tag]);
                              }
                            }}
                            className={`px-3 py-1 text-sm rounded-full transition-colors ${
                              tags.includes(tag)
                                ? 'bg-yellow-600 text-white'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                            }`}
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Importance Notes */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Notes (Optional)
                      </label>
                      <textarea
                        value={importanceNotes}
                        onChange={(e) => setImportanceNotes(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="Why is this receipt important? E.g., 'Fridge warranty expires in 1 year'"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Importance Details - View Mode (Always Expanded) */}
            {!isEditMode && (receipt.importance_level !== 'normal' || receipt.tags?.length > 0 || receipt.expiration_date || receipt.importance_notes) && (
              <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-md">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                  <Star className="h-4 w-4 mr-2 text-yellow-600" />
                  Importance Details
                </h3>

                <div className="space-y-3">
                  {/* Importance Level */}
                  {receipt.importance_level && receipt.importance_level !== 'normal' && (
                    <div className="flex items-center">
                      <AlertTriangle className="h-4 w-4 mr-2 text-gray-600 dark:text-gray-400" />
                      <span className="text-sm text-gray-700 dark:text-gray-300 mr-2">Level:</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        receipt.importance_level === 'critical'
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                      }`}>
                        {receipt.importance_level.charAt(0).toUpperCase() + receipt.importance_level.slice(1)}
                      </span>
                    </div>
                  )}

                  {/* Expiration Date */}
                  {receipt.expiration_date && (
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-600 dark:text-gray-400" />
                      <span className="text-sm text-gray-700 dark:text-gray-300 mr-2">Expires:</span>
                      <span className="text-sm text-gray-900 dark:text-white">
                        {formatDate(receipt.expiration_date)}
                      </span>
                      {(() => {
                        const expDate = new Date(receipt.expiration_date);
                        const today = new Date();
                        const daysUntilExpiration = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));

                        if (daysUntilExpiration < 0) {
                          return <span className="ml-2 text-xs text-red-600 dark:text-red-400">(Expired)</span>;
                        } else if (daysUntilExpiration <= 30) {
                          return <span className="ml-2 text-xs text-orange-600 dark:text-orange-400">(Expires soon: {daysUntilExpiration} days)</span>;
                        } else if (daysUntilExpiration <= 90) {
                          return <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400">({daysUntilExpiration} days remaining)</span>;
                        }
                        return null;
                      })()}
                    </div>
                  )}

                  {/* Tags */}
                  {receipt.tags && receipt.tags.length > 0 && (
                    <div className="flex items-start">
                      <Tag className="h-4 w-4 mr-2 mt-1 text-gray-600 dark:text-gray-400" />
                      <div className="flex-1">
                        <span className="text-sm text-gray-700 dark:text-gray-300 mr-2">Tags:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {receipt.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {receipt.importance_notes && (
                    <div className="flex items-start">
                      <AlertCircle className="h-4 w-4 mr-2 mt-1 text-gray-600 dark:text-gray-400" />
                      <div className="flex-1">
                        <span className="text-sm text-gray-700 dark:text-gray-300 mr-2">Notes:</span>
                        <p className="text-sm text-gray-900 dark:text-white mt-1 whitespace-pre-wrap">
                          {receipt.importance_notes}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Total */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-lg font-medium text-gray-900 dark:text-white">Total Amount</span>
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(totalAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer — only rendered in edit mode (view-mode actions moved to header) */}
        {isEditMode && (
        <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
              {/* Edit Mode - Desktop layout */}
              <div className="hidden md:flex justify-between">
                <button
                  type="button"
                  onClick={() => setShowVersionHistory(true)}
                  className="inline-flex items-center justify-center px-4 py-2 border border-indigo-300 dark:border-indigo-600 rounded-md text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Version History
                </button>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditMode(false);
                      setMerchant(receipt.merchant || '');
                      setReceiptDate(receipt.receipt_date?.split('T')[0] || '');
                      setSelectedAccountId(currentAccount?.id);
                      setItems(receipt.items || []);
                      setError(null);
                      // Reset importance fields
                      setImportanceLevel(receipt.importance_level || 'normal');
                      setExpirationDate(receipt.expiration_date?.split('T')[0] || '');
                      setImportanceNotes(receipt.importance_notes || '');
                      setTags(receipt.tags || []);
                    }}
                    disabled={saving}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Edit Mode - Mobile layout */}
              <div className="md:hidden space-y-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setShowVersionHistory(true)}
                    className="inline-flex items-center justify-center px-3 py-2 border border-indigo-300 dark:border-indigo-600 rounded-md text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                  >
                    <Clock className="h-4 w-4 mr-1" />
                    <span className="text-xs">History</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditMode(false);
                      setMerchant(receipt.merchant || '');
                      setReceiptDate(receipt.receipt_date?.split('T')[0] || '');
                      setSelectedAccountId(currentAccount?.id);
                      setItems(receipt.items || []);
                      setError(null);
                      // Reset importance fields
                      setImportanceLevel(receipt.importance_level || 'normal');
                      setExpirationDate(receipt.expiration_date?.split('T')[0] || '');
                      setImportanceNotes(receipt.importance_notes || '');
                      setTags(receipt.tags || []);
                    }}
                    disabled={saving}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    <span className="text-xs">Cancel</span>
                  </button>
                </div>
              </div>
        </div>
        )}

        <ConfirmationModal />
        <ToastContainer />

        {/* Version History Modal */}
        <ReceiptVersionHistoryModal
          isOpen={showVersionHistory}
          onClose={() => setShowVersionHistory(false)}
          receiptId={receipt?.id}
          onVersionRestored={() => {
            setShowVersionHistory(false);
            if (onReceiptUpdated) {
              onReceiptUpdated();
            }
          }}
        />

        {/* Match Transaction Modal */}
        <LinkModal
          isOpen={isLinkModalOpen}
          onClose={() => setIsLinkModalOpen(false)}
          receipt={receipt}
          onLinked={() => {
            setIsLinkModalOpen(false);
            if (onReceiptUpdated) {
              onReceiptUpdated();
            }
          }}
        />

        {/* Receipt Photo Lightbox */}
        {isLightboxOpen && receipt?.receipt_image_url && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
            onClick={() => setIsLightboxOpen(false)}
          >
            <div
              className="relative max-w-4xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setIsLightboxOpen(false)}
                className="absolute top-2 right-2 z-10 bg-white dark:bg-gray-800 rounded-full p-2 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </button>

              {/* Receipt Image */}
              <div className="relative">
                <AuthImage
                  src={receipt.receipt_image_url}
                  alt="Receipt Full Size"
                  className="w-full max-h-[80vh] object-contain"
                />
              </div>
            </div>
          </div>
        )}
    </BaseModal>
  );
};

export default ReceiptModal;
